import { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { MarkdownRenderer } from '@/components/ui/markdown-renderer'
import { FieldImprovementsContainer } from '@/components/ui/field-improvement'
import { Loader2, X, Check, AlertCircle, Sparkles, BarChart3 } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'

interface EnhancementData {
  original: {
    question: string
    answer: string
    tags: string[]
    question_variations?: string[]
  }
  enhanced: {
    question: string
    answer: string
    tags: string[]
    question_variations?: string[]
  }
  changes_summary: {
    question_changes: string
    answer_changes: string
    tags_changes: string
    content_check?: string
    warning?: string
  }
  reasoning?: {
    question_decision: 'kept' | 'refined' | 'replaced'
    alignment: string
    strategy: 'REFINE' | 'REBUILD'
  }
  validation?: {
    errorCodes: { original: number; enhanced: number }
    warnings: { original: number; enhanced: number }
    links: { original: number; enhanced: number }
    topicAlignment: any
  }
  faqId: string
}

interface EnhancementPreviewProps {
  faqId: string
  onApply: (enhanced: EnhancementData['enhanced']) => void
  onCancel: () => void
  onFieldApply?: (field: string, value: any) => void
}

export function EnhancementPreview({
  faqId,
  onApply,
  onCancel,
  onFieldApply
}: EnhancementPreviewProps) {
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<EnhancementData | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [acceptedFields, setAcceptedFields] = useState<Set<string>>(new Set())
  const [acceptedValues, setAcceptedValues] = useState<Record<string, any>>({})
  const [isApplying, setIsApplying] = useState(false)
  const { toast } = useToast()

  // Auto-fetch on mount
  useEffect(() => {
    fetchEnhancement()
  }, [faqId])

  const fetchEnhancement = async () => {
    setLoading(true)
    setError(null)

    try {
      const response = await fetch('/api/knowledge/enhance', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ faqId })
      })

      // Check if response is OK first
      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`API Error (${response.status}): ${errorText}`)
      }

      // Get response text first to debug
      const responseText = await response.text()

      if (!responseText || responseText.trim() === '') {
        throw new Error('Empty response from API')
      }

      // Try to parse JSON
      let result
      try {
        result = JSON.parse(responseText)
      } catch (parseError) {
        console.error('JSON Parse Error:', parseError)
        console.error('Response Text:', responseText)
        throw new Error(`Invalid JSON response: ${responseText.substring(0, 100)}...`)
      }

      if (!result.success) {
        throw new Error(result.error || 'Enhancement failed')
      }

      setData(result)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Enhancement failed'
      setError(errorMessage)
      toast({
        title: 'Enhancement fehlgeschlagen',
        description: errorMessage,
        variant: 'destructive'
      })
    } finally {
      setLoading(false)
    }
  }

  const handleFieldApply = (field: string, value: any) => {
    if (!data) return

    console.log('[DEBUG EnhancementPreview] handleFieldApply called for field:', field)
    console.log('[DEBUG EnhancementPreview] Field value:', value)

    // Only mark as accepted, don't save yet
    setAcceptedFields(prev => new Set([...prev, field]))
    setAcceptedValues(prev => ({ ...prev, [field]: value }))

    console.log('[DEBUG EnhancementPreview] Field marked as accepted, NOT calling onFieldApply')

    toast({
      title: `${field.charAt(0).toUpperCase() + field.slice(1)} markiert`,
      description: 'Klicke "Alle Übernehmen" zum Speichern'
    })
  }

  const handleFieldReject = (field: string) => {
    setAcceptedFields(prev => {
      const newSet = new Set(prev)
      newSet.delete(field)
      return newSet
    })
    setAcceptedValues(prev => {
      const { [field]: removed, ...rest } = prev
      return rest
    })
  }

  const handleApplyAll = () => {
    if (!data) return

    console.log('[DEBUG EnhancementPreview] handleApplyAll called')
    console.log('[DEBUG EnhancementPreview] Accepted fields:', Array.from(acceptedFields))
    console.log('[DEBUG EnhancementPreview] Accepted values:', acceptedValues)

    // Build enhanced object from accepted values + original for non-accepted
    const enhancedToApply = {
      question: acceptedValues.question ?? data.enhanced.question,
      answer: acceptedValues.answer ?? data.enhanced.answer,
      tags: acceptedValues.tags ?? data.enhanced.tags,
      question_variations: acceptedValues.variations ?? data.enhanced.question_variations
    }

    console.log('[DEBUG EnhancementPreview] Calling onApply with:', enhancedToApply)

    // Call onApply which will save and close modal
    onApply(enhancedToApply)

    toast({
      title: acceptedFields.size === improvements.length ? 'Alle Enhancements übernommen' : `${acceptedFields.size} Verbesserungen übernommen`,
      description: 'Artikel wird gespeichert...'
    })
  }

  if (loading) {
    return (
      <Card className="w-full">
        <CardContent className="flex items-center justify-center min-h-[300px]">
          <div className="text-center space-y-4">
            <Loader2 className="h-12 w-12 animate-spin mx-auto text-primary" />
            <div>
              <p className="text-sm font-medium text-foreground">
                Smart-Improvement wird geladen...
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                KI analysiert und optimiert Ihren Artikel
              </p>
            </div>
            <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground">
              <span>Powered by Groq Llama-4-Scout</span>
            </div>
          </div>
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card className="w-full border-destructive">
        <CardContent className="p-6">
          <div className="text-center space-y-4">
            <AlertCircle className="h-8 w-8 mx-auto text-destructive" />
            <div>
              <p className="font-medium text-destructive">Enhancement fehlgeschlagen</p>
              <p className="text-sm text-muted-foreground mt-2">{error}</p>
            </div>
            <div className="flex gap-2 justify-center">
              <Button variant="outline" onClick={onCancel}>
                Abbrechen
              </Button>
              <Button onClick={fetchEnhancement}>
                Erneut versuchen
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    )
  }

  if (!data) return null

  // Prepare improvements data for FieldImprovementsContainer
  // Note: autoAccept is now used ONLY for recommendations, not automatic application
  const improvements: Array<{
    type: 'question' | 'answer' | 'tags' | 'variations'
    before: string | string[]
    after: string | string[]
    autoAccept: boolean // Used for showing recommendations only
  }> = [
    {
      type: 'question',
      before: data.original.question,
      after: data.enhanced.question,
      autoAccept: data.reasoning?.question_decision !== 'kept' &&
                 data.reasoning?.strategy === 'REFINE' // Show recommendation if AI refined the question
    },
    {
      type: 'answer',
      before: data.original.answer,
      after: data.enhanced.answer,
      autoAccept: false // Answers should always be reviewed manually
    },
    {
      type: 'tags',
      before: data.original.tags,
      after: data.enhanced.tags,
      autoAccept: data.enhanced.tags.every(tag => tag.split(/\s+/).length === 2) &&
                 data.enhanced.tags.length >= 3 &&
                 data.enhanced.tags.length <= 5 // Show recommendation if tags are well-formatted
    }
  ]

  // Add variations if they exist
  if (data.enhanced.question_variations && data.enhanced.question_variations.length > 0) {
    improvements.push({
      type: 'variations',
      before: data.original.question_variations || [],
      after: data.enhanced.question_variations,
      autoAccept: true // Variations are generally safe to recommend
    })
  }

  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-yellow-500" />
            Smart-Improvement Ergebnisse
          </CardTitle>
          <div className="flex items-center gap-2">
            {data.validation && (() => {
              // Calculate quality: 100% base, deduct points for issues
              let qualityScore = 100

              // Deduct for errors (critical)
              qualityScore -= data.validation.errorCodes.enhanced * 20

              // Deduct for warnings (moderate)
              qualityScore -= data.validation.warnings.enhanced * 10

              // Bonus for having links (references are good)
              qualityScore += Math.min(data.validation.links.enhanced * 5, 20)

              // Clamp between 0-100
              qualityScore = Math.max(0, Math.min(100, qualityScore))

              return (
                <Badge variant="outline" className="text-xs">
                  <BarChart3 className="h-3 w-3 mr-1" />
                  Qualität: {Math.round(qualityScore)}%
                </Badge>
              )
            })()}
            <Button
              variant="ghost"
              size="sm"
              onClick={onCancel}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Warning if present */}
        {data.changes_summary.warning && (
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-500 mt-0.5" />
              <div>
                <p className="font-medium text-yellow-800 dark:text-yellow-200 text-sm">Hinweis</p>
                <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                  {data.changes_summary.warning}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Field Improvements */}
        <FieldImprovementsContainer
          improvements={improvements}
          onFieldAccept={handleFieldApply}
          onFieldReject={handleFieldReject}
          disabled={isApplying}
        />

        {/* Reasoning & Validation */}
        {(data.reasoning || data.validation) && (
          <div className="border rounded-lg p-4 bg-muted/20 space-y-3">
            <h3 className="font-semibold text-sm flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              Validierung & Reasoning
            </h3>

            {data.reasoning && (
              <div className="text-xs space-y-1">
                <div className="flex items-center gap-2">
                  <Badge variant="outline">{data.reasoning.strategy}</Badge>
                  <span className="text-muted-foreground">
                    Question {data.reasoning.question_decision}
                  </span>
                </div>
                <p className="text-muted-foreground">{data.reasoning.alignment}</p>
              </div>
            )}

            {data.validation && (
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div className="p-2 bg-background rounded border">
                  <div className="text-muted-foreground">Error-Codes</div>
                  <div className="font-mono">
                    {data.validation.errorCodes.original} → {data.validation.errorCodes.enhanced}
                    {data.validation.errorCodes.original === data.validation.errorCodes.enhanced ? ' ✅' : ' ❌'}
                  </div>
                </div>
                <div className="p-2 bg-background rounded border">
                  <div className="text-muted-foreground">Warnings</div>
                  <div className="font-mono">
                    {data.validation.warnings.original} → {data.validation.warnings.enhanced}
                    {data.validation.warnings.original === data.validation.warnings.enhanced ? ' ✅' : ' ❌'}
                  </div>
                </div>
                <div className="p-2 bg-background rounded border">
                  <div className="text-muted-foreground">Links</div>
                  <div className="font-mono">
                    {data.validation.links.original} → {data.validation.links.enhanced}
                    {data.validation.links.original <= data.validation.links.enhanced ? ' ✅' : ' ❌'}
                  </div>
                </div>
              </div>
            )}

            {data.changes_summary.content_check && (
              <div className="text-xs font-mono text-muted-foreground border-t pt-2">
                {data.changes_summary.content_check}
              </div>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center justify-between pt-4 border-t">
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={isApplying}
          >
            <X className="mr-2 h-4 w-4" />
            Abbrechen
          </Button>

          <div className="flex items-center gap-2">
            {acceptedFields.size > 0 && (
              <div className="text-sm text-muted-foreground mr-2">
                {acceptedFields.size} ausgewählt
              </div>
            )}

            <Button
              onClick={handleApplyAll}
              className="bg-green-600 hover:bg-green-700"
              disabled={isApplying || acceptedFields.size === 0}
            >
              <Check className="mr-2 h-4 w-4" />
              💾 Speichern {acceptedFields.size > 0 && `(${acceptedFields.size})`}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
